import React, { useState } from 'react';
import { FormikProps } from 'formik';
import capitalize from 'lodash/capitalize';

import { Trans, useTranslation } from '@lyearn/i18n';
import { DefaultModal } from '@lyearn/molecules';
import { LinkButton, useSnackbar } from '@lyearn/ui';
import BaseForm from '@/components/Form';
import { Link } from '@/components/Link';
import useDefaultErrorSnackbar from '@/hooks/useDefaultErrorSnackbar';
import useResponse from '@/hooks/useResponse';
import routes from '@/pages/corporate/routes';
import { MeetingTypeEnum } from '@/types/schema';
import { getErrorMessage } from '@/utils/api/response';

import { useCreateMeetingMutation } from '../../graphql/mutation/generated/createMeeting';
import useMeetingEvents from '../../hooks/useMeetingEvents';
import { EMPTY_TEMPLATE_ID } from '../SelectTemplateMenu';
import { DeleteDialogProps } from './types';
import { Create1on1FormInput, getFields, initialValues, validateMeetingForm } from './utils';

const FORM_OVERRIDES = { spacing: { row: 'space-y-24' } };

const Setup1on1Modal: React.FC<DeleteDialogProps> = (props) => {
  const { onClose, user, onSuccess, createMeetingPayload } = props;
  const { t } = useTranslation(['1on1s']);
  const selectedValues = React.useRef<typeof initialValues>({
    assigneeId: user ? [user.id] : [],
    frequency: initialValues.frequency,
    templateId: initialValues.templateId,
  });
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const { showDefaultErrorSnackbar } = useDefaultErrorSnackbar();
  const { handleResponse } = useResponse();
  const [{ fetching }, createMeeting] = useCreateMeetingMutation();
  const [submitRef, setSubmitRef] = useState<() => void>(() => {});
  const { onTrackOneOnOneEvent } = useMeetingEvents();

  const onSubmit = React.useCallback(async () => {
    const res = await createMeeting({
      data: {
        ...(createMeetingPayload ?? {}),
        participantIds: selectedValues.current.assigneeId,
        schedule: selectedValues.current.frequency,
        templateId:
          selectedValues.current.templateId === EMPTY_TEMPLATE_ID
            ? null
            : selectedValues.current.templateId,
        type: MeetingTypeEnum.OneOnOne,
      },
    });
    const username = res?.data?.createMeeting?.participantsProfile?.[0].name;
    handleResponse({
      condition: !res.error,
      onSuccess: () => {
        onTrackOneOnOneEvent('onSchedule1on1');
        showSnackbar({
          message: <Trans i18nKey="1on1s:Modal.SetupMeetingSnackBar" values={{ name: username }} />,
          action: (
            <Link to={routes.oneOnOneDetail(res?.data?.createMeeting?.id!)}>
              <LinkButton className="flex-shrink-0 py-2 px-4" color={'inverse'}>
                {t('Common:Actions.View')}
              </LinkButton>
            </Link>
          ),
        });
        onSuccess?.();
        onClose();
      },
      onError: () => {
        if (typeof res.error?.graphQLErrors?.[0]?.message === 'string') {
          showErrorSnackbar({ message: capitalize(getErrorMessage(res.error?.graphQLErrors)) });
        } else {
          showDefaultErrorSnackbar();
        }
      },
    });
  }, [
    createMeeting,
    createMeetingPayload,
    handleResponse,
    onTrackOneOnOneEvent,
    showSnackbar,
    t,
    onSuccess,
    onClose,
    showErrorSnackbar,
    showDefaultErrorSnackbar,
  ]);

  const onChange = React.useCallback(({ values }: FormikProps<Create1on1FormInput>) => {
    selectedValues.current = values;
  }, []);

  const fields = React.useMemo(() => getFields({ t, showReporteeDetails: true }), [t]);
  const validate = React.useMemo(
    () => (values: Create1on1FormInput) => validateMeetingForm(t, values),
    [t],
  );

  return (
    <DefaultModal
      header={{
        title: t('1on1s:Modal.SetupMeetingTitle'),
        description: t('1on1s:Modal.SetupMeetingDescription'),
      }}
      primaryAction={{
        label: t('Common:Calendar.Schedule'),
        onClick: submitRef,
        loading: fetching,
      }}
      onClose={onClose}>
      <BaseForm<Create1on1FormInput>
        fields={fields}
        formName="SetupMeeting"
        initialValues={selectedValues.current}
        overrides={FORM_OVERRIDES}
        setSubmitRef={setSubmitRef}
        validate={validate}
        onChange={onChange}
        onSubmit={onSubmit}
      />
    </DefaultModal>
  );
};

export default Setup1on1Modal;
